import React, { useState, forwardRef, useRef } from 'react';
import styled from 'styled-components';
import apiClient from '../../api';
import ReCAPTCHA from 'react-google-recaptcha';

// Importando seus componentes reutilizáveis
import { Button } from '../../components/common/Button';
import { FormGroup, Label, Input, Message } from '../../components/common/Forms';
import { AuthFormCard, Title } from '../../components/common/AuthLayout';

const FormContainer = styled.section`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 5rem 2rem;
  background-color: ${({ theme }) => theme.colors.background};
`;

const Subtitle = styled.p`
  font-size: 1.1rem;
  color: ${({ theme }) => theme.colors.light};
  text-align: center;
  max-width: 400px;
  margin: -2rem auto 2rem;
  line-height: 1.6;
`;

const PhoneInputWrapper = styled.div`
  display: flex;
  gap: 10px;

  & > div:first-child {
    flex: 0 0 80px; 
  }
  
  & > div:last-child {
    flex: 1;
  }
`;

const ErrorText = styled.span`
  color: #ff4d4f;
  font-size: 0.85rem;
  margin-top: 0.25rem;
  display: block;
`;

const RequestDemoForm = forwardRef((props, ref) => {
  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  
  // Campos opcionais agora
  const [nomeEmpresa, setNomeEmpresa] = useState('');
  const [instagram, setInstagram] = useState(''); 

  const [ddi, setDdi] = useState('+55');
  const [telefone, setTelefone] = useState('');

  const [mensagem, setMensagem] = useState({ text: '', type: '' });
  const [fieldErrors, setFieldErrors] = useState({});
  
  const [isLoading, setIsLoading] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const recaptchaRef = useRef(null);

  // --- Funções de Validação ---
  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(String(email).toLowerCase());
  };

  const validatePhone = (phone) => {
    const numbers = phone.replace(/\D/g, '');
    return numbers.length >= 10 && numbers.length <= 11;
  };

  // --- Máscara de Telefone ---
  const handlePhoneChange = (e) => {
    let value = e.target.value;
    value = value.replace(/\D/g, "");
    if (value.length > 11) value = value.slice(0, 11);

    if (value.length > 2) {
      value = `(${value.substring(0, 2)}) ${value.substring(2)}`;
    }
    if (value.length > 9) { 
       value = `${value.substring(0, 10)}-${value.substring(10)}`;
    }

    setTelefone(value);
    
    if (fieldErrors.telefone) {
      setFieldErrors(prev => ({ ...prev, telefone: null }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = {};

    if (!validateEmail(email)) {
      errors.email = 'Por favor, insira um e-mail válido.';
    }

    if (!validatePhone(telefone)) {
      errors.telefone = 'Por favor, insira um telefone válido com DDD.';
    }

    if (!recaptchaToken) {
      setMensagem({ text: 'Por favor, confirme que você não é um robô.', type: 'error' });
      return;
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setIsLoading(true);
    setMensagem({ text: '', type: '' });
    setFieldErrors({});

    try {
      const telefoneCompleto = `${ddi}${telefone.replace(/\D/g, '')}`;

      await apiClient.post('/api/solicitar-contato', {
        nome: nome,
        email: email,
        empresa: nomeEmpresa, // Pode ir vazio agora
        instagram: instagram, // Novo campo
        telefone: telefoneCompleto, 
        recaptcha_token: recaptchaToken
      });

      setMensagem({
        text: 'Solicitação enviada! Entraremos em contato em breve.',
        type: 'success',
      });
      
      // Limpa formulário
      setNome('');
      setEmail('');
      setNomeEmpresa('');
      setInstagram('');
      setTelefone('');
      setDdi('+55');

    } catch (error) {
      setMensagem({
        text: error.response?.data?.error || 'Erro ao enviar solicitação.',
        type: 'error',
      });
    } finally {
      setIsLoading(false);
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
      setRecaptchaToken(null);
    }
  };

  return (
    <FormContainer ref={ref}>
      <AuthFormCard>
        <Title>Fale com um Especialista</Title>
        <Subtitle>
          Preencha seus dados e nossa equipe entrará em contato para agendar uma conversa.
        </Subtitle>
        
        <form onSubmit={handleSubmit}>
          <FormGroup>
            <Label htmlFor="nome">Seu Nome *</Label>
            <Input
              id="nome"
              type="text"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              required
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="email">Seu Email Profissional *</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if(fieldErrors.email) setFieldErrors(prev => ({...prev, email: null}));
              }}
              required
              placeholder="voce@suaempresa.com"
            />
            {fieldErrors.email && <ErrorText>{fieldErrors.email}</ErrorText>}
          </FormGroup>

          {/* Empresa agora é Opcional */}
          <FormGroup>
            <Label htmlFor="empresa">Nome da Empresa <small>(Opcional)</small></Label>
            <Input
              id="empresa"
              type="text"
              value={nomeEmpresa}
              onChange={(e) => setNomeEmpresa(e.target.value)}
              // required removido
            />
          </FormGroup>
          
          {/* Novo Campo Instagram */}
          <FormGroup>
            <Label htmlFor="instagram">Instagram da Marca <small>(Opcional)</small></Label>
            <Input
              id="instagram"
              type="text"
              value={instagram}
              onChange={(e) => setInstagram(e.target.value)}
              placeholder="@seu.perfil"
            />
          </FormGroup>

          <Label htmlFor="telefone">Seu Telefone *</Label>
          <PhoneInputWrapper>
            <FormGroup>
                <Input 
                    type="text" 
                    value={ddi} 
                    onChange={(e) => setDdi(e.target.value)}
                    maxLength={4} 
                    style={{textAlign: 'center'}}
                    aria-label="DDI"
                />
            </FormGroup>

            <FormGroup>
                <Input
                id="telefone"
                type="tel"
                value={telefone}
                onChange={handlePhoneChange}
                required
                placeholder="(11) 99999-9999"
                />
            </FormGroup>
          </PhoneInputWrapper>
          {fieldErrors.telefone && <ErrorText style={{marginTop: '-1rem', marginBottom: '1rem'}}>{fieldErrors.telefone}</ErrorText>}

          <FormGroup style={{ display: 'flex', justifyContent: 'center' }}>
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
              onChange={(token) => setRecaptchaToken(token)}
              onExpired={() => setRecaptchaToken(null)}
            />
          </FormGroup>

          <Button type="submit" style={{ width: '100%' }} disabled={isLoading}>
            {isLoading ? 'Enviando...' : 'Solicitar Contato'}
          </Button>
        </form>

        {mensagem.text && (
          <Message type={mensagem.type}>{mensagem.text}</Message>
        )}
      </AuthFormCard>
    </FormContainer>
  );
});

export default RequestDemoForm;